/**
 * Public asset URL resolution.
 *
 * Assets uploaded through the admin library live in the private
 * `partner-assets` bucket, so their storage URLs 404 for visitors. Anything
 * that points at that bucket (or has only a `file_path`) is routed through the
 * `public-asset-file` edge function, which checks the asset is published +
 * public and redirects to a short-lived signed URL.
 *
 * Since Stage 2 of the document pipeline, `public-asset-file` resolves the file
 * through `asset.current_version_id` (falling back to the asset row), and hands
 * `html`-kind versions to `render-document`. That resolution is entirely
 * server-side: callers here keep passing an asset id and get the current
 * version automatically. `assetDocumentUrl` addresses the same asset by slug.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const isPrivateBucket = (value: string) => value.includes("/partner-assets/");


export function assetFileUrl(
  assetId: string,
  fileUrl?: string | null,
  filePath?: string | null,
  opts?: { download?: boolean },
): string | null {
  const url = fileUrl?.trim();
  // Downloads must always pass through the resolver so the response carries
  // Content-Disposition, including files that still live in a public bucket.
  if (opts?.download) {
    return `${SUPABASE_URL}/functions/v1/public-asset-file?id=${assetId}&download=1`;
  }
  if (url && !isPrivateBucket(url)) return url;
  // Document-only assets (Stage 7) carry neither `file_url` nor `file_path` —
  // their bytes live on `asset_version` and are rendered on demand. The
  // resolver handles all three shapes, so always hand it the asset id rather
  // than returning null, which callers treat as "no file" and filter out.
  return `${SUPABASE_URL}/functions/v1/public-asset-file?id=${assetId}`;
}



/**
 * Slug-addressed URL for the current version of a document.
 * `file` versions are served as the stored file; `html` versions are rendered
 * on demand as a PDF. Since Stage 4c `format=html` 301s to the canonical web
 * page at `/resources/<slug>`, so use a plain route link for the web surface.
 * Existing id-based URLs above remain valid and unchanged.
 */
export function assetDocumentUrl(
  slug: string,
  opts?: { download?: boolean },
): string {
  const params = new URLSearchParams({ slug, format: "pdf" });
  if (opts?.download) params.set("download", "1");
  return `${SUPABASE_URL}/functions/v1/render-document?${params.toString()}`;
}


/**
 * Forces a browser download rather than opening the file in a new tab.
 * The resolver adds a Content-Disposition attachment header via the signed
 * URL, so a plain anchor click saves the file.
 */
export async function triggerFileDownload(url: string, filename?: string) {
  // Fetching first avoids browsers ignoring `download` on a cross-origin URL
  // and prevents PDF extensions from opening the response in a new tab.
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

  const blob = await response.blob();
  if (!blob.size) throw new Error("The downloaded file was empty");

  const objectUrl = URL.createObjectURL(blob);
  const disposition = response.headers.get("content-disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const basicName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const resolvedName = encodedName
    ? decodeURIComponent(encodedName)
    : basicName || filename || "download";
  const extensionByType: Record<string, string> = {
    "application/pdf": ".pdf",
    "application/zip": ".zip",
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "video/mp4": ".mp4",
  };
  const downloadName = /\.[a-z0-9]{2,8}$/i.test(resolvedName)
    ? resolvedName
    : `${resolvedName}${extensionByType[blob.type] || ""}`;

  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = downloadName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

export function assetThumbnailUrl(
  assetId: string,
  thumbnailUrl?: string | null,
): string | null {
  const url = thumbnailUrl?.trim();
  if (!url) return null;
  if (!isPrivateBucket(url)) return url;
  // Carry the stored cache-buster through to the resolver URL, otherwise the
  // browser (and the function's 5-minute Cache-Control) keeps serving the old
  // preview after a file replace. The function ignores unknown params.
  const version = url.match(/[?&]v=([^&#]+)/)?.[1];
  const base = `${SUPABASE_URL}/functions/v1/public-asset-file?id=${assetId}&kind=thumb`;
  return version ? `${base}&v=${encodeURIComponent(decodeURIComponent(version))}` : base;
}